import { DateTime } from 'luxon';
import { FormattedRelativeTime } from 'react-intl';
import { Tooltip } from '@mantine/core';

import { DateTimeDisplay } from 'ui-components/components/date-time-display/date-time-display';

import { MDash } from 'ui-components/components/mdash/mdash';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86_400;

type RelativeTimeUnit = 'second' | 'minute' | 'hour' | 'day';

const toRelativeParts = (timestamp: string): { value: number; unit: RelativeTimeUnit } | null => {
  const elapsedSeconds = DateTime.fromISO(timestamp).diffNow('seconds').seconds;
  if (!Number.isFinite(elapsedSeconds)) {
    return null;
  }

  const magnitude = Math.abs(elapsedSeconds);

  if (magnitude < SECONDS_PER_MINUTE) {
    return { value: Math.round(elapsedSeconds), unit: 'second' };
  }

  if (magnitude < SECONDS_PER_HOUR) {
    return { value: Math.round(elapsedSeconds / SECONDS_PER_MINUTE), unit: 'minute' };
  }

  if (magnitude < SECONDS_PER_DAY) {
    return { value: Math.round(elapsedSeconds / SECONDS_PER_HOUR), unit: 'hour' };
  }

  return { value: Math.round(elapsedSeconds / SECONDS_PER_DAY), unit: 'day' };
};

type RelativeTimeProps = {
  timestamp: string;
};

export const RelativeTime = ({ timestamp }: RelativeTimeProps) => {
  const relativeParts = toRelativeParts(timestamp);

  if (!relativeParts) {
    return <MDash />;
  }

  return (
    <Tooltip label={<DateTimeDisplay utcTimestamp={timestamp} />} withArrow>
      <span>
        <FormattedRelativeTime numeric='auto' unit={relativeParts.unit} value={relativeParts.value} />
      </span>
    </Tooltip>
  );
};
